import gql from "graphql-tag";

/* -------------------------------------------------------------------------- */
/*                                   BATCHES                                  */
/* -------------------------------------------------------------------------- */

const PHOTO_UPLOAD_BATCH_FIELDS = gql`
    fragment PhotoUploadBatchFields on ModerationPhotoBatchListType {
        id
        containsReleaseOrEditorialFlags
        containsExplicitOrTextFlags
        containsSavedForLater
        createdAt
        moderationStatus
        releaseCount
        submittedAt
        moderator {
            id
            displayName
            username
            isPhotoModerator
            isManager
        }
        photoShoot {
            id
            title
            titleApproved
        }
        creator {
            id
            name
            username
            avatarUrl
            isActive
            isPremiumPhotographer
        }
        creatorTier {
            internalDisplayName
        }
    }
`;

// sortNewestToOldest flag if set to True will reverse default sort
export const FETCH_BATCHES = gql`
    ${PHOTO_UPLOAD_BATCH_FIELDS}
    query FetchBatches(
        $limit: Int! = 10
        $offset: Int! = 0
        $moderationType: ModerationType! = ANY
        $showOnlySuspendedCreators: Boolean! = false
        $textSearch: String!
        $hasNoFlags: Boolean
        $hasUploadsWithRelOrEuoFlag: Boolean
        $hasUploadsWithExpOrTxtFlag: Boolean
        $hasSavedForLaterUploads: Boolean
        $isUnassigned: Boolean
        $assignedToMe: Boolean
        $sortNewestToOldest: Boolean! = false
        $batchIds: [ID!]
    ) {
        batches: listPhotoUploadBatches(
            limit: $limit
            offset: $offset
            moderationType: $moderationType
            showOnlySuspendedCreators: $showOnlySuspendedCreators
            textSearch: $textSearch
            hasNoFlags: $hasNoFlags
            hasUploadsWithRelOrEuoFlag: $hasUploadsWithRelOrEuoFlag
            hasUploadsWithExpOrTxtFlag: $hasUploadsWithExpOrTxtFlag
            hasSavedForLaterUploads: $hasSavedForLaterUploads
            isUnassigned: $isUnassigned
            assignedToMe: $assignedToMe
            sortNewestToOldest: $sortNewestToOldest
            batchIds: $batchIds
        ) {
            totalCount
            items {
                ...PhotoUploadBatchFields
            }
        }
    }
`;

export const FETCH_SUSPENDED_BATCHES = gql`
    ${PHOTO_UPLOAD_BATCH_FIELDS}
    query FetchSuspendedBatches($showOnlySuspendedCreators: Boolean! = true) {
        batches: listPhotoUploadBatches(
            showOnlySuspendedCreators: $showOnlySuspendedCreators
        ) {
            totalCount
            items {
                ...PhotoUploadBatchFields
            }
        }
    }
`;

/* -------------------------------------------------------------------------- */
/*                                   UPLOADS                                  */
/* -------------------------------------------------------------------------- */

export const FETCH_UPLOADS_IDS_IN_BATCHES = gql`
    query getPhotoUploadIdsInBatches($batchIds: [ID!]!) {
        list: getPhotoUploadIdsInBatches(batchIds: $batchIds) {
            id: batchId
            uploadIds: photoUploadIds
        }
    }
`;

export const FETCH_UPLOADS_IN_BATCH = gql`
    query FetchUploadsInBatch($batchId: ID!, $limit: Int = 10000) {
        list: listPhotoUploadsInModerationBatch(
            batchId: $batchId
            limit: $limit
        ) {
            items {
                id
                aspectRatio
                thumbnail
                photoTitle
                photoTitleApproved
                photoDescription
                photoDescriptionApproved
                photoBatchId
                status
                originalFilename
                isGem: starred
                mature
                autoNudity
                autoViolence
                autoWarningText
                editorialUseOnly
                hasRecognizablePeopleOrProperty
                license
                userTags
                approvedUserTags
                warningUserTags
            }
        }
    }
`;

export const FETCH_PHOTO_COUNTS_FOR_BATCHES = gql`
    query GetPhotoCountsForBatches(
        $batchIds: [ID!]!
        $showOnlySuspendedCreators: Boolean! = false
    ) {
        counts: getPhotoCountsForBatches(
            batchIds: $batchIds
            showOnlySuspendedCreators: $showOnlySuspendedCreators
        ) {
            id
            fullyPublishedCount
            photoUploadCount
            visuallyPublishedCount
            inVisualModerationCount
            deniedCount
        }
    }
`;

export const FETCH_PHOTO_UPLOAD_URLS = gql`
    query FetchPhotoUploadUrls($photoUploadIds: [ID!]!) {
        fetchPhotoUploadUrls: refreshPhotoUploadsForModeration(
            photoUploadIds: $photoUploadIds
        ) {
            items {
                id
                thumbnail
                uploadedImageUrl
            }
        }
    }
`;

/* -------------------------------------------------------------------------- */
/*                            MODERATOR ASSIGNMENT                            */
/* -------------------------------------------------------------------------- */

// fetches profile details of signed in moderator
export const MY_MOD_PROFILE = gql`
    query MyPhotoModeratorProfile {
        profile: myPhotoModeratorProfile {
            id
            moderatorId
            isManager
        }
    }
`;

// fetches list of all eligible photo moderators
export const FETCH_MOD_LIST = gql`
    query FetchModList {
        modList: listModeratorsEligibleToReceivePhotoAssignments {
            items {
                id
                displayName
                avatarUrl
            }
        }
    }
`;

/* -------------------------------------------------------------------------- */
/*                                  RELEASES                                  */
/* -------------------------------------------------------------------------- */

export const FETCH_RELEASES_IN_BATCH = gql`
    query GetListReleasesInBatch(
        $status: UploadStatus = UPLOAD_COMPLETE
        $photoBatchId: ID!
        $limit: Int = 10000
    ) {
        releaseList: listReleasesInModerationPhotoBatch(
            status: $status
            photoBatchId: $photoBatchId
            limit: $limit
        ) {
            items {
                id
                contentType
                originalFilename
                batchId
            }
            totalCount
        }
    }
`;

export const FETCH_RELEASE_URLS = gql`
    query PhotoReleaseUrls($photoReleaseIds: [ID!]!) {
        urls: photoReleaseDetails(photoReleaseIds: $photoReleaseIds) {
            id
            downloadUrl
            originalFilename
        }
    }
`;
